/**
 * VaultDocEditor — the writing surface for Word documents, Markdown and text.
 *
 * Word files arrive as a paragraph/run document (see docModel), so this is a
 * real document editor: bold, italic, underline, text colour, font size,
 * paragraph styles (Title, Heading 1–3, Quote) and alignment, applied to the
 * selection and written back into the .docx run by run. Everything the editor
 * does not model — images, tables, headers, themes — is left untouched in the
 * original file rather than regenerated.
 *
 * Markdown and .txt keep a plain block surface, because those formats cannot
 * carry a colour or a heading level and pretending otherwise would silently
 * throw away what a person applied.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Popover, Select } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Doc, Para, Run, DEFAULT_STYLES, htmlToRuns, parseDoc, paraText, runsToHtml, serialiseDoc,
} from './docModel';

const COLOURS = [
  '000000', '434343', '666666', '999999', 'B7B7B7', 'FFFFFF',
  'C00000', 'E06666', 'B45F06', 'BF9000', '38761D', '1155CC', '674EA7',
];
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36];

interface Props {
  value: string;
  readOnly?: boolean;
  /** Markdown gets a monospace surface; Word gets prose typography. */
  variant?: 'document' | 'markdown';
  onChange: (next: string) => void;
}

/** Which built-in style a paragraph maps onto, for its on-screen look. */
const styleClass = (style?: string) =>
  (style || 'Normal').toLowerCase().replace(/\s+/g, '-');

export function VaultDocEditor(props: Props) {
  const rich = useMemo(() => parseDoc(props.value), [props.value]);
  return rich ? <RichDocEditor {...props} initial={rich} /> : <PlainDocEditor {...props} />;
}

// --------------------------------------------------------------------------
// Word documents
// --------------------------------------------------------------------------

function RichDocEditor({ value, readOnly, onChange, initial }: Props & { initial: Doc }) {
  const { t } = useLanguage();
  const [doc, setDoc] = useState<Doc>(initial);
  const [focus, setFocus] = useState(0);
  // Controlled so picking a colour closes the picker; leaving it open would
  // make Escape the only way out, and Escape closes the file window.
  const [picker, setPicker] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const lastPushed = useRef(value);
  const caretTo = useRef<{ index: number; atStart: boolean } | null>(null);

  useEffect(() => {
    if (value !== lastPushed.current) {
      const next = parseDoc(value);
      if (next) setDoc(next);
      lastPushed.current = value;
    }
  }, [value]);

  const push = useCallback((paragraphs: Para[]) => {
    const next: Doc = { ...doc, paragraphs };
    setDoc(next);
    const text = serialiseDoc(next);
    lastPushed.current = text;
    onChange(text);
  }, [doc, onChange]);

  const blockAt = (i: number) =>
    pageRef.current?.querySelector<HTMLDivElement>(`[data-p="${i}"]`) ?? null;

  /** Put the caret where an Enter or Backspace should leave it. */
  useLayoutEffect(() => {
    const want = caretTo.current;
    if (!want) return;
    caretTo.current = null;
    const el = blockAt(want.index);
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(want.atStart);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    setFocus(want.index);
  }, [doc]);

  const readBlock = (i: number): Run[] => {
    const el = blockAt(i);
    return el ? htmlToRuns(el) : doc.paragraphs[i]?.runs ?? [{ t: '' }];
  };

  /** Pull the typed HTML back into the model for one paragraph. */
  const syncBlock = (i: number) => {
    const runs = readBlock(i);
    push(doc.paragraphs.map((p, j) => (j === i ? { ...p, runs } : p)));
  };

  const applyCommand = (command: string, argument?: string) => {
    const el = blockAt(focus);
    if (!el) return;
    el.focus();
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand(command, false, argument);
    if (command === 'fontSize') {
      // execCommand only speaks 1–7. Apply the sentinel, then rewrite it as a
      // real point size so the run carries the size a person actually chose.
      el.querySelectorAll('font[size="7"]').forEach((node) => {
        const span = document.createElement('span');
        span.style.fontSize = `${argument}pt`;
        span.innerHTML = (node as HTMLElement).innerHTML;
        node.replaceWith(span);
      });
    }
    syncBlock(focus);
  };

  const setSize = (pt: number) => {
    const el = blockAt(focus);
    if (!el) return;
    el.focus();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand('fontSize', false, '7');
    el.querySelectorAll('font[size="7"]').forEach((node) => {
      const span = document.createElement('span');
      span.style.fontSize = `${pt}pt`;
      span.innerHTML = (node as HTMLElement).innerHTML;
      node.replaceWith(span);
    });
    syncBlock(focus);
  };

  const setStyle = (style: string) =>
    push(doc.paragraphs.map((p, j) => (j === focus ? { ...p, style } : p)));

  const setAlign = (align: Para['align']) =>
    push(doc.paragraphs.map((p, j) => (j === focus ? { ...p, align } : p)));

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, i: number) => {
    if (readOnly) return;
    const el = e.currentTarget;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      // Split this paragraph at the caret, carrying formatting into both halves.
      const tail = selection.getRangeAt(0).cloneRange();
      tail.setEndAfter(el.lastChild ?? el);
      const holder = document.createElement('div');
      holder.appendChild(tail.extractContents());
      const before = htmlToRuns(el);
      const after = htmlToRuns(holder);
      const current = doc.paragraphs[i];
      const next = doc.paragraphs.slice();
      // A new paragraph after a heading is body text, the way a document behaves.
      const inheritedStyle = /^(Title|Heading)/.test(current.style || '') ? 'Normal' : current.style;
      next.splice(i, 1,
        { ...current, runs: before },
        { runs: after, style: inheritedStyle, align: current.align });
      caretTo.current = { index: i + 1, atStart: true };
      push(next);
      return;
    }
    if (e.key === 'Backspace' && i > 0) {
      const selection = window.getSelection();
      const collapsedAtStart =
        selection?.isCollapsed &&
        selection.anchorOffset === 0 &&
        (selection.anchorNode === el || el.firstChild === selection.anchorNode ||
          (el.textContent ?? '') === '');
      if (collapsedAtStart) {
        e.preventDefault();
        const prev = doc.paragraphs[i - 1];
        const merged = [...readBlock(i - 1), ...readBlock(i)];
        const next = doc.paragraphs.slice();
        next.splice(i - 1, 2, { ...prev, runs: merged });
        caretTo.current = { index: i - 1, atStart: false };
        push(next);
      }
      return;
    }
    if (e.key === 'ArrowDown' && i < doc.paragraphs.length - 1) {
      const selection = window.getSelection();
      if (selection?.isCollapsed && selection.anchorOffset === (el.textContent ?? '').length) {
        e.preventDefault();
        caretTo.current = { index: i + 1, atStart: true };
        setDoc({ ...doc });
      }
    } else if (e.key === 'ArrowUp' && i > 0) {
      const selection = window.getSelection();
      if (selection?.isCollapsed && selection.anchorOffset === 0) {
        e.preventDefault();
        caretTo.current = { index: i - 1, atStart: false };
        setDoc({ ...doc });
      }
    }
  };

  const currentPara = doc.paragraphs[focus];

  const tool = (label: React.ReactNode, title: string, onClick: () => void, on = false) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={on}
      className={`vault-tool ${on ? 'on' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    <div className="vault-doc document rich">
      {!readOnly && (
        <div className="vault-doc-toolbar">
          <Select
            size="small"
            value={currentPara?.style || 'Normal'}
            style={{ width: 132 }}
            onChange={setStyle}
            getPopupContainer={(n) => n.parentElement as HTMLElement}
            options={(doc.styles.length ? doc.styles : DEFAULT_STYLES).map((s) => ({
              value: s, label: s,
            }))}
          />
          <Select
            size="small"
            value={undefined}
            placeholder={t('vault.viewer.fontSize')}
            style={{ width: 78 }}
            onChange={(v) => setSize(Number(v))}
            getPopupContainer={(n) => n.parentElement as HTMLElement}
            options={SIZES.map((s) => ({ value: s, label: String(s) }))}
          />
          <span className="vault-tool-group">
            {tool(<b>B</b>, t('vault.viewer.bold'), () => applyCommand('bold'))}
            {tool(<i>I</i>, t('vault.viewer.italic'), () => applyCommand('italic'))}
            {tool(<u>U</u>, t('vault.viewer.underline'), () => applyCommand('underline'))}
          </span>
          <Popover
            trigger="click"
            placement="bottom"
            title={t('vault.viewer.textColour')}
            open={picker}
            onOpenChange={setPicker}
            content={
              <div className="vault-swatches">
                {COLOURS.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    className="vault-swatch"
                    style={{ background: `#${hex}` }}
                    aria-label={`#${hex}`}
                    onClick={() => { setPicker(false); applyCommand('foreColor', `#${hex}`); }}
                  />
                ))}
              </div>
            }
          >
            <button
              type="button"
              className="vault-tool swatchy"
              title={t('vault.viewer.textColour')}
              aria-label={t('vault.viewer.textColour')}
            >
              A<i style={{ background: '#C00000' }} />
            </button>
          </Popover>
          <span className="vault-tool-group">
            {tool('⯇', t('vault.viewer.alignLeft'), () => setAlign('left'), currentPara?.align === 'left')}
            {tool('⯈', t('vault.viewer.alignCentre'), () => setAlign('center'), currentPara?.align === 'center')}
            {tool('⯆', t('vault.viewer.alignRight'), () => setAlign('right'), currentPara?.align === 'right')}
            {tool('☰', t('vault.viewer.alignJustify'), () => setAlign('justify'), currentPara?.align === 'justify')}
          </span>
        </div>
      )}

      <div className="vault-doc-page" ref={pageRef}>
        {doc.paragraphs.map((p, i) => (
          <DocBlock
            key={i}
            index={i}
            para={p}
            readOnly={readOnly}
            onFocus={() => setFocus(i)}
            onInput={() => syncBlock(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
          />
        ))}
      </div>

    </div>
  );
}

/** One paragraph. Its HTML is written imperatively so typing never loses the caret. */
function DocBlock({
  index, para, readOnly, onFocus, onInput, onKeyDown,
}: {
  index: number;
  para: Para;
  readOnly?: boolean;
  onFocus: () => void;
  onInput: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const html = runsToHtml(para.runs);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only rewrite when this block is not the one being typed into; otherwise
    // React would replace the node under the caret on every keystroke.
    if (document.activeElement === el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  return (
    <div
      ref={ref}
      data-p={index}
      dir="auto"
      role="textbox"
      aria-multiline="false"
      tabIndex={0}
      className={`vault-doc-block style-${styleClass(para.style)} ${paraText(para) ? '' : 'empty'}`}
      style={{ textAlign: para.align }}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onFocus={onFocus}
      onInput={onInput}
      onBlur={onInput}
      onKeyDown={onKeyDown}
    />
  );
}

// --------------------------------------------------------------------------
// Markdown and plain text
// --------------------------------------------------------------------------

function PlainDocEditor({ value, readOnly, variant = 'document', onChange }: Props) {
  const { t } = useLanguage();
  const [blocks, setBlocks] = useState<string[]>(() => (value ?? '').split('\n'));
  const lastPushed = useRef(value);
  const wrapRef = useRef<HTMLDivElement>(null);
  const focusAfter = useRef<number | null>(null);

  useEffect(() => {
    if (value !== lastPushed.current) {
      setBlocks((value ?? '').split('\n'));
      lastPushed.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (focusAfter.current == null) return;
    const i = focusAfter.current;
    focusAfter.current = null;
    const el = wrapRef.current?.querySelector<HTMLTextAreaElement>(`textarea[data-i="${i}"]`);
    el?.focus();
    el?.setSelectionRange(0, 0);
  }, [blocks]);

  const push = useCallback((next: string[]) => {
    setBlocks(next);
    const text = next.join('\n');
    lastPushed.current = text;
    onChange(text);
  }, [onChange]);

  const setBlock = (i: number, v: string) => push(blocks.map((b, j) => (j === i ? v : b)));

  const grow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>, i: number) => {
    const el = e.target as HTMLTextAreaElement;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const before = el.value.slice(0, el.selectionStart);
      const after = el.value.slice(el.selectionStart);
      const next = [...blocks];
      next.splice(i, 1, before, after);
      focusAfter.current = i + 1;
      push(next);
    } else if (e.key === 'Backspace' && el.selectionStart === 0 && el.selectionEnd === 0 && i > 0) {
      e.preventDefault();
      const prev = blocks[i - 1];
      const next = [...blocks];
      next.splice(i - 1, 2, prev + el.value);
      push(next);
      requestAnimationFrame(() => {
        const p = wrapRef.current?.querySelector<HTMLTextAreaElement>(`textarea[data-i="${i - 1}"]`);
        p?.focus();
        p?.setSelectionRange(prev.length, prev.length);
      });
    } else if (e.key === 'ArrowDown' && el.selectionStart === el.value.length) {
      const n = wrapRef.current?.querySelector<HTMLTextAreaElement>(`textarea[data-i="${i + 1}"]`);
      if (n) { e.preventDefault(); n.focus(); n.setSelectionRange(0, 0); }
    } else if (e.key === 'ArrowUp' && el.selectionStart === 0) {
      const p = wrapRef.current?.querySelector<HTMLTextAreaElement>(`textarea[data-i="${i - 1}"]`);
      if (p) { e.preventDefault(); p.focus(); p.setSelectionRange(p.value.length, p.value.length); }
    }
  };


  return (
    <div className={`vault-doc ${variant}`}>
      <div className="vault-doc-page" ref={wrapRef}>
        {blocks.map((b, i) => (
          <textarea
            key={i}
            data-i={i}
            dir="auto"
            rows={1}
            className={`vault-doc-block ${b.trim() === '' ? 'empty' : ''}`}
            value={b}
            readOnly={readOnly}
            placeholder={i === 0 && !b ? t('vault.viewer.docPlaceholder') : undefined}
            ref={grow}
            onInput={(e) => grow(e.currentTarget)}
            onChange={(e) => setBlock(i, e.target.value)}
            onKeyDown={(e) => onKey(e, i)}
          />
        ))}
      </div>
    </div>
  );
}

export default VaultDocEditor;
